import { useCallback, useEffect, useRef, useState } from 'react';
import './MicControl.css';
import { useOptionalBriefingRoomServices } from '../../contexts/BriefingRoomContext';
import { useOptionalTtsQueue } from '../../contexts/TtsQueueContext';
import { useMicActivation } from '../../hooks/useMicActivation';
import { SttUnavailableError, type SttSession } from '../../services/types';

export type MicState = 'idle' | 'recording' | 'transcribing' | 'error';

export interface MicControlProps {
  testidPrefix: string;
  onTranscript?: (text: string) => void;
}

/**
 * V2 Voice GM (2026-06-02) — mic button + status.
 *
 * Supports BOTH activation modes per the user's V2 design pick ("we need
 * both like the voice in the vibe designer work — we already have it").
 * Mirrors vibe-designer's VoiceControlsBar.tsx pattern:
 *   - toggle (default): click to start, click to stop.
 *   - ptt: mouseDown/touchStart → start; mouseUp/touchEnd/touchCancel → stop.
 * The activation mode comes from useMicActivation (drawer picker writes it).
 *
 * Lifecycle:
 *   idle → click/hold → recording → release/click → transcribing → idle
 *   any error path → error state, click to clear → idle
 *
 * The transcript is delivered via onTranscript callback when STT completes.
 * Caller (ScenarioRunner) routes it into useVoiceLog. Mic permission
 * denied (SttUnavailableError) flips to error state with the reason
 * shown inline.
 */
export function MicControl({ testidPrefix, onTranscript }: MicControlProps) {
  const services = useOptionalBriefingRoomServices();
  const ttsQueue = useOptionalTtsQueue();
  const [activation] = useMicActivation();
  const [state, setState] = useState<MicState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sessionRef = useRef<SttSession | null>(null);

  const cleanup = useCallback(() => {
    if (sessionRef.current !== null) {
      sessionRef.current.cancel();
      sessionRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const start = useCallback(async () => {
    if (services === null || !services.voice.isAvailable()) {
      return;
    }
    if (state !== 'idle' && state !== 'error') {
      return;
    }
    // V4 (2026-06-02): the magic moment — mic press cancels all in-flight
    // TTS so the player can "cut the GM off" mid-narration. The cancel
    // is unconditional: even with no autoplay running, calling cancelAll
    // on an idle queue is a no-op.
    if (ttsQueue !== null) {
      ttsQueue.cancelAll();
    }
    setState('recording');
    setErrorMessage(null);
    try {
      const session = await services.voice.startStt();
      sessionRef.current = session;
      session.onError((err) => {
        setErrorMessage(
          err instanceof SttUnavailableError ? err.reason : err.message,
        );
        setState('error');
        sessionRef.current = null;
      });
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof SttUnavailableError
          ? err.reason
          : err instanceof Error
            ? err.message
            : String(err),
      );
      setState('error');
    }
  }, [services, ttsQueue, state]);

  const stopAndTranscribe = useCallback(async () => {
    if (sessionRef.current === null) {
      setState('idle');
      return;
    }
    setState('transcribing');
    try {
      const text = await sessionRef.current.stop();
      sessionRef.current = null;
      if (text.length > 0 && onTranscript !== undefined) {
        onTranscript(text);
      }
      setState('idle');
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof SttUnavailableError
          ? err.reason
          : err instanceof Error
            ? err.message
            : String(err),
      );
      setState('error');
      sessionRef.current = null;
    }
  }, [onTranscript]);

  // Toggle-mode handler: same button serves start + stop.
  const handleClick = useCallback(() => {
    if (activation !== 'toggle') {
      return;
    }
    if (state === 'idle' || state === 'error') {
      void start();
    } else if (state === 'recording') {
      void stopAndTranscribe();
    }
  }, [activation, state, start, stopAndTranscribe]);

  // PTT-mode handlers — bound to mouseDown/Up + touchStart/End.
  const handlePttDown = useCallback(() => {
    if (activation !== 'ptt') {
      return;
    }
    void start();
  }, [activation, start]);

  const handlePttUp = useCallback(() => {
    if (activation !== 'ptt') {
      return;
    }
    if (state === 'recording') {
      void stopAndTranscribe();
    }
  }, [activation, state, stopAndTranscribe]);

  if (services === null || !services.voice.isAvailable()) {
    return null;
  }

  const isRecording = state === 'recording';
  const isTranscribing = state === 'transcribing';
  const isError = state === 'error';
  const glyph = isRecording ? '●' : isTranscribing ? '…' : '🎤';
  const label =
    activation === 'ptt'
      ? isRecording
        ? 'Release to stop recording'
        : 'Hold to talk'
      : isRecording
        ? 'Stop recording'
        : isTranscribing
          ? 'Transcribing'
          : 'Start recording';

  return (
    <div
      className="br-mic"
      data-testid={testidPrefix}
      data-state={state}
      data-activation={activation}
    >
      <button
        type="button"
        className="br-mic-toggle"
        data-testid={`${testidPrefix}-toggle`}
        onClick={handleClick}
        onMouseDown={handlePttDown}
        onMouseUp={handlePttUp}
        onMouseLeave={handlePttUp}
        onTouchStart={handlePttDown}
        onTouchEnd={handlePttUp}
        onTouchCancel={handlePttUp}
        disabled={isTranscribing}
        aria-pressed={isRecording}
        aria-label={label}
        title={label}
      >
        <span className="br-mic-glyph" aria-hidden="true">
          {glyph}
        </span>
      </button>
      {isError && errorMessage !== null && (
        <p
          className="br-label br-mic-error"
          data-testid={`${testidPrefix}-error`}
          role="alert"
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

export default MicControl;
